/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import ReactJsonView from 'react-json-view' // Npm: React json pretty.
import _ from 'underscore' // Npm: underscore library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { HiBarsArrowDown } from 'react-icons/hi2' // Npm: React Icons for icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Box,
  Button,
  Flex,
  Text,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.


/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import VendorAccountSelector from 'components/VendorAccountSelector'
import { MemoizedSelect } from 'components/MemoizedInput'
import Modal from 'components/Modal'


/*
 * GRAPHS
 */
import LogsReadQuery from './__query__/index.logs.read.query'


/*
 * OBJECTS
 */
const Index = () => {

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({})
  const [smppId, setSmppId] = React.useState('')
  const [debuggingLevel, setDebuggingLevel] = React.useState(['ALL'])
  const [pduToShow, setPduToShow] = React.useState({})
  const _tableHeaderHeightRef = React.useRef(0)
  const { isOpen: isPduModalOpen, onOpen: onPduModalOpen, onClose: onPduModalClose } = useDisclosure()
  const _QueryLogsRead = useQuery(LogsReadQuery, {
    'variables': {
      smppId,
      'severity': _.isEmpty(_.first(debuggingLevel)) || '' === _.first(debuggingLevel) ? null : _.first(debuggingLevel),
      'skip': paginationModel.pageSize * paginationModel.page,
      'take': paginationModel.pageSize
    },
    'fetchPolicy': Object.React.App.fetchPolicy,
    'pollInterval': Object.React.App.pollInterval
  })
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Data assignment.
  const columns = React.useMemo(() => [
    {
      'field': 'sno',
      'headerName': 'S.No',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'disableColumnMenu': true,
      'sortable': false,
      'filterable': false
    },
    {
      'field': 'severity',
      'headerName': 'Severity',
      'description': 'Log severity level',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'minWidth': 200,
      'flex': 1,
      'disableColumnMenu': true,
      renderCell: ({ row }) => (
        <Flex
          w='100%'
          h='100%'
          align='center'
          justify='center'
          bg={'INFO' === row.severity ? 'green.50' : 'ERROR' === row.severity || 'BIND' === row.severity || 'UNBIND' === row.severity ? 'red.50' : 'CLOSE' === row.severity ? 'yellow.50' : 'gray.100'
          }>
          {row.severity}
        </Flex>
      )
    },
    {
      'field': 'message',
      'headerName': 'Message',
      'description': 'SMPP error message',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 400
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Log creation timestamp',
      'editable': false,
      'sortable': true,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'minWidth': 200,
      'flex': 1,
      'disableColumnMenu': true
    },
    {
      'field': 'pdu',
      'headerName': 'PDU',
      'description': 'Protocol Data Unit',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 100,
      'disableColumnMenu': true,
      renderCell: ({ row }) => (
        <Flex w='100%' h='100%' align='center' justify='center'>
          <Button
            size='sm'
            variant='ghost'
            color='brand.500'
            fontWeight={600}
            onClick={() => {
              setPduToShow(row?.pduData ?? {})
              onPduModalOpen()
            }}
          >
            View PDU
          </Button>
        </Flex>
      )
    }
  ], [])

  const rows = React.useMemo(() => _.compact(_QueryLogsRead?.data?.LogsRead?.map((r, index) => 'READ_SUCCESSFUL' === r.status ? ({
    'id': r?.id || `log-${index}-${Date.now()}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'severity': r?.severity || '-',
    'message': Object.React.App.smppError[r?.output?.command_status] || '-',
    'createdAt': r?.createdAt ? Moment(r.createdAt).format('YYYY-MM-DD hh:mm:ss A') : '-',
    'pduData': r?.output
  }) : null)), [_QueryLogsRead?.data?.LogsRead, paginationModel.page, paginationModel.pageSize])

  // Return component.
  return (
    <>
      <Flex className='ratePlan base'>
        <Flex
          display='flex'
          flex={1}
          flexDirection='column'
          gap={_isCurrentViewMobile ? '12px' : '22px'}
          bg='white'
          maxH='82vh'
          minH='82vh'
          borderRadius='20px'
          p={_isCurrentViewMobile ? '12px' : '22px'}>
          <Flex w='100%' pb='0px' alignItems='end' justifyContent='space-between'>
            <Flex
              w='100%'
              flexDir={{ 'base': 'column', 'md': 'row' }}
              justifyContent='space-between'
              alignItems='center'
              gap='22px'
              zIndex={10}>
              <Flex flex={0.7} flexDir='row' gap='6px' align='center'>
                <Flex color='purple.600'><HiBarsArrowDown size={26} /></Flex>
                <Text fontSize='2xl' fontWeight={300} color='brand.600'>Vendor <Text as='span' color='brand.400'>Smpp</Text> <Text as='span' color='brand.500'>Logs</Text></Text>
              </Flex>
              <Flex
                flex={0.5}
                flexDir='row'
                gap='12px'
                justifyContent='flex-end'
                align='center'>
                <Flex mt='0' width='100%' flex={1}>
                  <VendorAccountSelector
                    placeholder='Select Vendor Account'
                    onChange={j => setSmppId(j.target.value?.split?.('(')?.[1]?.split?.(')')?.[0])}
                    isRequired={false}
                  />
                </Flex>
                <Flex width='100%' flex={1}>
                  <MemoizedSelect
                    placeholder='Select Log Level'
                    onChange={j => setDebuggingLevel([j.target.value])}
                    options={_.without(Object.React.App.enums.SMPP_DEBUGGING_LEVEL.enums.map(i => i.key), 'NONE')}
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>

          {/* MUI DataGrid */}
          <DataGrid
            key={`${paginationModel.page}-${paginationModel.pageSize}`}
            rows={rows}
            columns={columns}
            rowHeight={80}
            rowCount={_.pluck(_QueryLogsRead?.data?.LogsRead, '_totalCount')?.[0] || 0}
            paginationMode='server'
            filterMode='client'
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[8, 20, 50, 100]}
            slots={{
              'loadingOverlay': () => <TableSpinner isLoading={true} isEmpty={false} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noResultsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />,
              'noRowsOverlay': () => <TableSpinner isLoading={false} isEmpty={true} chopHeightFromHundredPercentage={_tableHeaderHeightRef?.current?.clientHeight} />
            }}
            disableRowSelectionOnClick
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={newModel => setColumnVisibilityModel(newModel)}
            loading={_QueryLogsRead?.loading}
            disableColumnMenu={false}
          />

          <Modal
            title='PDU Report'
            isOpen={isPduModalOpen}
            onClose={onPduModalClose}
          >
            <Box maxH='80vh' overflowY='auto'>
              <ReactJsonView
                src={pduToShow ?? {}}
                enableClipboard={copy => navigator.clipboard.writeText(JSON.stringify(copy.src, null, '\t'))}
              />
            </Box>
          </Modal>
        </Flex>
      </Flex>
    </>
  )
}

/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORTS
 */
export default Index
